import { CarouselCalendar } from "@repo/ui/components/carousel-calendar";
import { useLocale, useTranslations } from "next-intl";
import X from "@/assets/icons/x.svg";
import { ListingItemCateringImage } from "@/components/listing/listing-item-catering-image";
import { Pill } from "@/components/pill";
import { DialogClose, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AddDietToCart } from "@/features/diets/components/diet-add-to-cart";
import { DietListItemPricing } from "@/features/diets/components/diet-list-item/diet-list-item-pricing";
import type { DietCatalog, DietCatalogDetail } from "@/features/diets/types";
import type { DietPricingComplex } from "@/features/diets/types/diet-pricing-complex.type";
import { getNextDates } from "@/lib/date-helpers";
import {
  formatCateringName,
  formatDietName,
} from "@/lib/helpers/format-brand-name";
import { useDietDates } from "@/lib/hooks/use-diet-dates";
import { getLocalePrice } from "@/lib/utilities";

type DietMenuModalHeaderProps = {
  data: DietCatalogDetail | DietCatalog;
  currentDate: Date;
  setCurrentDate: (date: Date) => void;
  configuration?: Record<string, string | number | string[]>;
  showCta?: boolean;
  activeMealIngredients: string | null;
  activeMealName: string | null;
  numberOfDays?: number;
  price?: number;
  priceComplex?: DietPricingComplex;
  type?: "multidiet" | "default";
  selectedCalories?: string;
};

export const DietMenuModalHeader = ({
  data,
  currentDate,
  setCurrentDate,
  configuration,
  showCta,
  activeMealIngredients,
  activeMealName,
  numberOfDays,
  price,
  priceComplex,
  type,
  selectedCalories,
}: DietMenuModalHeaderProps) => {
  const t = useTranslations();
  const locale = useLocale();
  const dates = getNextDates(14);
  const dietDates = useDietDates({
    diet: data,
    saturdays: true,
    sundays: true,
    locale,
  });
  const handleChangeDate = (date: Date) => {
    setCurrentDate(date);
  };
  const tagsExist = data.tags.length > 0;

  if (activeMealIngredients) {
    return (
      <DialogHeader className="relative mt-[60px] bg-brand-grey4 px-4 py-4 md:px-18">
        <DialogTitle className="text-left font-medium text-brand-green1-300 text-xl leading-7">
          {activeMealName}
        </DialogTitle>
        {tagsExist && (
          <div className="flex flex-wrap gap-2 pt-1">
            {data.tags.map((tag, index) => (
              <>
                <Pill
                  className="rounded-sm border bg-brand-white p-2 font-medium text-brand-grey5 text-sm capitalize"
                  key={tag.slug + index.toString()}
                >
                  {t(`dietTags.${tag.slug}`)}
                </Pill>
              </>
            ))}
          </div>
        )}
      </DialogHeader>
    );
  }

  return (
    <>
      <DialogHeader className="relative mt-[60px] h-max overflow-hidden bg-brand-grey4 p-4 pr-0 md:mt-0 md:px-8 md:pt-8 md:pb-4">
        <DialogClose className="absolute top-8 right-8 hidden md:block">
          <X />
        </DialogClose>
        <div className="flex flex-col gap-4 md:flex-row md:items-center">
          <ListingItemCateringImage
            className="hidden size-[86px] md:flex"
            imageUrl={data.catering.logo.url}
            name={formatCateringName(data.catering.name)}
          />

          <div>
            <DialogTitle className="text-left">
              <p className="font-normal text-sm leading-[22px]">
                {formatCateringName(data.catering.name)}
              </p>
              <p className="font-medium text-brand-green1-300 text-xl leading-8 md:text-2xl">
                {formatDietName(data.name)}
              </p>
            </DialogTitle>
            <div className="mt-2 flex flex-wrap items-center gap-x-4 text-base text-brand-black">
              {type !== "multidiet" ? (
                <div className="flex">
                  <span className="mr-1">{t("diets.from")}</span>
                  <DietListItemPricing
                    discountCodes={data.discountCodes}
                    numberOfDays={numberOfDays}
                    price={price ?? data.basePrice}
                    priceComplex={priceComplex}
                    showTitle={false}
                  />
                </div>
              ) : (
                <p className="whitespace-nowrap">
                  <strong className="mr-1 inline-block font-bold leading-6">
                    {getLocalePrice({ price: data.basePrice })}
                  </strong>
                  <span>/ {t("diets.day")}</span>
                </p>
              )}
              <p className="whitespace-nowrap">
                <span className="mr-1">{t("diets.available_on")}</span>
                <span className="inline-block font-bold capitalize">
                  {dietDates.nextPossibleDayFormatEeee(new Date())}
                </span>
              </p>
              {showCta && (
                <AddDietToCart
                  button={{ variant: "link" }}
                  className="h-0! p-0 pt-3 sm:mt-0 sm:pt-0"
                  configuration={configuration}
                  diet={data}
                  dietSlug={data.slug}
                  kcal={selectedCalories}
                />
              )}
            </div>
          </div>
        </div>

        <div className="mt-4 md:mt-6">
          <CarouselCalendar
            className="p-0 md:pr-1"
            currentDate={currentDate}
            dates={dates.map((date) => new Date(date))}
            setCurrentDate={(date: string) => handleChangeDate(new Date(date))}
          />
        </div>
      </DialogHeader>
    </>
  );
};
